"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Database, ScanLine, Smartphone, Zap } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const LogicNode = ({
  label,
  icon: Icon,
}: {
  label: string;
  icon: React.ElementType;
}) => (
  <div className="logic-node flex items-center gap-2 px-4 py-2 rounded-full border border-orange-500/30 bg-[#0A0A0A] z-20 opacity-0 translate-y-4">
    <Icon className="w-3 h-3 text-orange-500" />
    <span className="text-orange-500 text-[10px] font-bold tracking-widest">
      {label}
    </span>
  </div>
);

const SmartCoreGraphic = () => {
  const containerRef = useRef(null);
  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });
      tl.to(".logic-node", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.1,
        ease: "back.out(1.7)",
      })
        .to(
          ".connector-path",
          {
            strokeDashoffset: 0,
            duration: 1,
            stagger: 0.1,
            ease: "power2.out",
          },
          "-=0.3",
        )
        .to(
          ".flow-path",
          { strokeDashoffset: -100, duration: 1.5, repeat: -1, ease: "none" },
          "<",
        );
      gsap.to(".pulse-ring", {
        scale: 1.5,
        opacity: 0,
        duration: 2,
        stagger: { each: 0.5, repeat: -1 },
        ease: "power1.out",
      });
    },
    { scope: containerRef },
  );

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col items-center justify-center w-full max-w-[500px]"
    >
      <div className="flex justify-between w-full max-w-[400px] mb-8 relative z-20">
        <LogicNode label="VERIFY" icon={Database} />
        <LogicNode label="LOG" icon={Database} />
        <LogicNode label="SYNC" icon={Database} />
        <LogicNode label="AUTH" icon={Database} />
      </div>
      <svg
        className="absolute top-[18px] left-0 w-full h-[80px] pointer-events-none z-10"
        viewBox="0 0 500 80"
        fill="none"
      >
        <defs>
          <path id="p1" d="M 60 10 V 35 Q 60 55 200 55" />
          <path id="p2" d="M 160 10 V 30 Q 160 55 235 55" />
          <path id="p3" d="M 340 10 V 30 Q 340 55 265 55" />
          <path id="p4" d="M 440 10 V 35 Q 440 55 300 55" />
        </defs>
        <g stroke="#333" strokeWidth="1" fill="none">
          <use href="#p1" />
          <use href="#p2" />
          <use href="#p3" />
          <use href="#p4" />
        </g>
        <g
          className="connector-group"
          stroke="#F97316"
          strokeWidth="1"
          fill="none"
          strokeOpacity="0.5"
        >
          <use
            href="#p1"
            className="connector-path"
            strokeDasharray="200"
            strokeDashoffset="200"
          />
          <use
            href="#p2"
            className="connector-path"
            strokeDasharray="200"
            strokeDashoffset="200"
          />
          <use
            href="#p3"
            className="connector-path"
            strokeDasharray="200"
            strokeDashoffset="200"
          />
          <use
            href="#p4"
            className="connector-path"
            strokeDasharray="200"
            strokeDashoffset="200"
          />
        </g>
        <g
          stroke="white"
          strokeWidth="2"
          fill="none"
          strokeDasharray="4 96"
          strokeLinecap="round"
        >
          <use href="#p1" className="flow-path" />
          <use href="#p2" className="flow-path" />
          <use href="#p3" className="flow-path" />
          <use href="#p4" className="flow-path" />
        </g>
      </svg>
      <div className="relative w-full h-[200px] rounded-3xl border border-white/10 bg-[#050505] flex flex-col items-center justify-center overflow-visible z-20">
        <div className="absolute -top-4 flex items-center gap-2 px-6 py-2 rounded-full border border-orange-500/30 bg-[#111] z-20 shadow-xl">
          <Zap className="w-3 h-3 text-orange-500 fill-orange-500" />
          <span className="text-white text-xs font-clash font-bold tracking-widest">
            SMART CORE LOGIC
          </span>
        </div>
        <div className="absolute bottom-0 w-full h-full overflow-hidden rounded-3xl z-0">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-orange-500/5 blur-[50px] rounded-full" />
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="pulse-ring absolute rounded-full border-t border-orange-500/30 bg-orange-500/5 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2"
              style={{ width: 100, height: 100 }}
            />
          ))}
        </div>
        <div className="relative z-10 text-center space-y-1">
          <div className="text-sm text-gray-500 font-mono">Status: ACTIVE</div>
          <div className="text-lg text-orange-300 font-clash font-semibold">
            99.9% Uptime
          </div>
        </div>
        <div className="absolute -bottom-6 w-12 h-12 bg-[#0A0A0A] border-t border-orange-500 rounded-full flex items-center justify-center z-20 shadow-[0_-5px_20px_rgba(249,115,22,0.4)]">
          <span className="text-[10px] font-clash font-semibold text-orange-300">
            SmartDwara
          </span>
        </div>
      </div>
    </div>
  );
};

const HowItWorksSection = () => {
  const sectionRef = useRef(null);

  useGSAP(
    () => {
      gsap.from(".hiw-title, .hiw-desc", {
        y: 30,
        opacity: 0,
        duration: 0.8,
        stagger: 0.2,
        scrollTrigger: { trigger: ".hiw-header", start: "top 80%" },
      });
      const trafficDots = gsap.utils.toArray<HTMLElement>(".bg-traffic-dot");
      trafficDots.forEach((dot) => {
        gsap.set(dot, { opacity: 0 });
        const tl = gsap.timeline({
          repeat: -1,
          delay: gsap.utils.random(0, 5),
        });
        tl.to(dot, {
          left: "100%",
          duration: gsap.utils.random(2, 6),
          ease: "none",
        });
        gsap.fromTo(
          dot,
          { left: "0%", opacity: 0 },
          {
            left: "100%",
            duration: gsap.utils.random(2.5, 5),
            ease: "none",
            repeat: -1,
            delay: gsap.utils.random(0, 4),
            keyframes: {
              "0%": { opacity: 0 },
              "10%": { opacity: 1 },
              "90%": { opacity: 1 },
              "100%": { opacity: 0 },
            },
          },
        );
      });
    },
    { scope: sectionRef },
  );

  return (
    <section
      ref={sectionRef}
      className="how-it-works py-32 bg-[#080808] px-4 border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        <div className="hiw-header text-center mb-24">
          <h2 className="hiw-title text-4xl md:text-6xl font-clash font-bold tracking-normal text-white mb-6">
            The Invisible{" "}
            <span className="bg-linear-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
              Nervous System.
            </span>
          </h2>
          <p className="hiw-desc text-gray-400 max-w-2xl mx-auto text-lg">
            See how Smart Dwara processes entry requests in milliseconds using
            our encrypted cloud architecture.
          </p>
        </div>
        <div className="relative w-full grid grid-cols-1 md:grid-cols-3 gap-8 items-center justify-items-center">
          <div className="absolute top-1/2 left-[10%] w-[80%] -translate-y-1/2 hidden md:block z-0 pointer-events-none">
            <div className="relative w-full h-12 flex items-center">
              <div className="w-full h-0.5 border-t-2 border-dashed border-white/10 absolute" />
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="bg-traffic-dot absolute w-2 h-2 bg-orange-500 rounded-full top-1/2 -translate-y-1/2 shadow-[0_0_10px_rgba(249,115,22,0.8)]"
                  style={{ left: 0 }}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col items-center gap-6 relative z-10 bg-[#080808] p-4 rounded-full">
            <div className="w-24 h-24 rounded-[2rem] bg-[#0A0A0A] border border-white/10 flex items-center justify-center shadow-[0_0_40px_rgba(0,0,0,0.5)] relative group">
              <ScanLine className="w-10 h-10 text-gray-400 group-hover:text-white transition-colors" />
            </div>
            <div className="text-center">
              <h3 className="text-white font-clash font-semibold text-xl">
                The Gate
              </h3>
              <p className="text-sm text-gray-500 mt-2">Scans QR / Reads Tag</p>
            </div>
          </div>
          <div className="relative w-full flex justify-center py-10 md:py-0 z-10">
            <div className="absolute inset-0 bg-orange-500/5 blur-[80px] rounded-full pointer-events-none" />
            <SmartCoreGraphic />
          </div>
          <div className="flex flex-col items-center gap-6 relative z-10 bg-[#080808] p-4 rounded-full">
            <div className="w-24 h-24 rounded-[2rem] bg-[#0A0A0A] border border-white/10 flex items-center justify-center shadow-[0_0_40px_rgba(0,0,0,0.5)] relative group">
              <Smartphone className="w-10 h-10 text-gray-400 group-hover:text-white transition-colors" />
            </div>
            <div className="text-center">
              <h3 className="text-white font-clash font-semibold text-xl">
                Your Phone
              </h3>
              <p className="text-sm text-gray-500 mt-2">Instant Notification</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
